// src/pages/DiscoverQuebec.tsx
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  FaArrowRight,
  FaBell,
  FaCheckCircle,
  FaChevronRight,
  FaCompass,
  FaConciergeBell,
  FaFire,
  FaGift,
  FaGlobeAmericas,
  FaHeart,
  FaHotel,
  FaLeaf,
  FaMapMarkedAlt,
  FaMountain,
  FaPlane,
  FaMugHot,
  FaRoute,
  FaShoppingBag,
  FaSnowflake,
  FaSun,
  FaTicketAlt,
  FaTree,
  FaUtensils,
} from 'react-icons/fa';
import SeoHead from '../components/SeoHead';

import MiniGuideAI from '../components/MiniGuideAI';
type DiscoverLanguage = 'fr' | 'en';

type DiscoverCopy = {
  seoTitle: string;
  seoDescription: string;
  eyebrow: string;
  title: string;
  titleAccent: string;
  subtitle: string;
  primaryCta: string;
  secondaryCta: string;
  alertCta: string;
  heroBadge: string;
  heroCardTitle: string;
  heroCardText: string;
  stats: { value: string; label: string }[];
  introTitle: string;
  introText: string;
  profileTitle: string;
  profileSubtitle: string;
  seasonTitle: string;
  seasonSubtitle: string;
  regionsTitle: string;
  regionsSubtitle: string;
  monetizationTitle: string;
  monetizationSubtitle: string;
  journeyTitle: string;
  journeySubtitle: string;
  reassuranceTitle: string;
  reassuranceSubtitle: string;
  finalTitle: string;
  finalSubtitle: string;
  exploreExperiences: string;
  viewMap: string;
  offerGiftCard: string;
  activateAlerts: string;
  discoverLastCall: string;
  joinPresale: string;
  prepareStay: string;
  routeLabel: string;
  fromAbroad: string;
  localPartner: string;
  bilingual: string;
  simplePlanning: string;
};

const copy: Record<DiscoverLanguage, DiscoverCopy> = {
  fr: {
    seoTitle: 'Découvrir le Québec autrement | Forfeo Explore',
    seoDescription:
      'Préparez votre séjour au Québec avec Forfeo Explore : expériences locales, séjours, restaurants, offres Last Call, préventes, cartes-cadeaux et alertes voyage.',
    eyebrow: 'Guide premium Forfeo Explore',
    title: 'Découvrir le Québec',
    titleAccent: 'autrement',
    subtitle:
      'Des expériences locales, des séjours, des restaurants, des escapades et des moments à offrir — réunis dans un seul univers pour planifier avant, vivre sur place et prolonger le souvenir après le voyage.',
    primaryCta: 'Explorer les expériences',
    secondaryCta: 'Voir la carte interactive',
    alertCta: 'Recevoir les alertes voyage',
    heroBadge: 'Pensé pour les visiteurs, familles, couples et voyageurs curieux',
    heroCardTitle: 'Une porte d’entrée vers le Québec local',
    heroCardText:
      'Forfeo aide les voyageurs à trouver des idées crédibles, réserver ou offrir des moments, et découvrir des partenaires locaux avant même leur arrivée.',
    stats: [
      { value: '4 saisons', label: 'd’idées locales' },
      { value: '1 carte', label: 'pour offrir le Québec' },
      { value: 'FR / EN', label: 'pour voyager simplement' },
    ],
    introTitle: 'Une page pensée pour convertir l’inspiration en action',
    introText:
      'Cette section devient une vitrine touristique et commerciale : elle inspire les visiteurs étrangers, les touristes canadiens et les Québécois, puis les guide vers le catalogue, la carte, les offres Last Call, les préventes, les alertes Évasion et la carte-cadeau Forfeo.',
    profileTitle: 'Choisir selon votre style de voyage',
    profileSubtitle:
      'Chaque profil mène naturellement vers les expériences, forfaits et partenaires les plus pertinents.',
    seasonTitle: 'Explorer le Québec par saison',
    seasonSubtitle:
      'Le Québec se vend différemment selon le moment du voyage. Forfeo transforme chaque saison en occasion de réserver, offrir ou découvrir.',
    regionsTitle: 'Des régions à explorer avant d’arriver',
    regionsSubtitle:
      'Une logique claire pour découvrir les régions, visualiser les offres et créer un itinéraire simple autour des partenaires locaux.',
    monetizationTitle: 'Des portes d’entrée monétisables pour Forfeo',
    monetizationSubtitle:
      'Chaque bloc renvoie vers un produit existant ou stratégique : carte-cadeau, boutique, Last Call, prévente, alertes, carte et expériences locales.',
    journeyTitle: 'Le parcours idéal du visiteur étranger',
    journeySubtitle:
      'Découvrir Forfeo avant le voyage, planifier quelques moments, recevoir des alertes et utiliser Forfeo sur place.',
    reassuranceTitle: 'Pourquoi passer par Forfeo',
    reassuranceSubtitle:
      'Un univers local, clair et rassurant pour éviter de perdre du temps entre plusieurs sites, liens et recherches dispersées.',
    finalTitle: 'Prêt à découvrir le Québec avec Forfeo ?',
    finalSubtitle:
      'Commencez par explorer les expériences, activer vos alertes ou offrir une carte Forfeo à quelqu’un qui arrive bientôt au Québec.',
    exploreExperiences: 'Explorer les expériences',
    viewMap: 'Voir la carte',
    offerGiftCard: 'Offrir une carte Forfeo',
    activateAlerts: 'Activer les alertes',
    discoverLastCall: 'Découvrir Last Call',
    joinPresale: 'Voir les préventes',
    prepareStay: 'Préparer mon séjour',
    routeLabel: 'Parcours voyageur',
    fromAbroad: 'Depuis l’étranger',
    localPartner: 'Partenaires locaux',
    bilingual: 'Expérience bilingue',
    simplePlanning: 'Planification simple',
  },
  en: {
    seoTitle: 'Discover Quebec differently | Forfeo Explore',
    seoDescription:
      'Plan your Quebec trip with Forfeo Explore: local experiences, stays, restaurants, Last Call offers, presales, gift cards and smart travel alerts.',
    eyebrow: 'Premium Forfeo Explore guide',
    title: 'Discover Quebec',
    titleAccent: 'differently',
    subtitle:
      'Local experiences, stays, restaurants, escapes and giftable moments — gathered in one place to plan before arrival, enjoy during the trip and keep the memory alive afterward.',
    primaryCta: 'Explore experiences',
    secondaryCta: 'View interactive map',
    alertCta: 'Get travel alerts',
    heroBadge: 'Built for visitors, families, couples and curious travellers',
    heroCardTitle: 'A gateway to local Quebec',
    heroCardText:
      'Forfeo helps travellers find credible ideas, book or gift memorable moments, and discover local partners before they even arrive.',
    stats: [
      { value: '4 seasons', label: 'of local ideas' },
      { value: '1 card', label: 'to gift Quebec' },
      { value: 'FR / EN', label: 'for easier travel' },
    ],
    introTitle: 'A page built to turn inspiration into action',
    introText:
      'This section becomes both a tourism showcase and a conversion page: it inspires international visitors, Canadian travellers and locals, then guides them to the catalogue, map, Last Call offers, presales, Évasion alerts and the Forfeo gift card.',
    profileTitle: 'Choose by travel style',
    profileSubtitle:
      'Each profile naturally points travellers toward the most relevant experiences, bundles and local partners.',
    seasonTitle: 'Explore Quebec by season',
    seasonSubtitle:
      'Quebec sells differently depending on the travel moment. Forfeo turns every season into a reason to book, gift or discover.',
    regionsTitle: 'Regions to explore before arrival',
    regionsSubtitle:
      'A simple way to discover regions, view offers and build an easy itinerary around local partners.',
    monetizationTitle: 'Monetizable entry points for Forfeo',
    monetizationSubtitle:
      'Each block routes to an existing or strategic product: gift card, shop, Last Call, presales, alerts, map and local experiences.',
    journeyTitle: 'The ideal journey for international visitors',
    journeySubtitle:
      'Discover Forfeo before the trip, plan a few moments, get alerts and use Forfeo once in Quebec.',
    reassuranceTitle: 'Why use Forfeo',
    reassuranceSubtitle:
      'A local, clear and reassuring ecosystem that avoids jumping between scattered websites, links and searches.',
    finalTitle: 'Ready to discover Quebec with Forfeo?',
    finalSubtitle:
      'Start by exploring experiences, activating alerts or gifting a Forfeo card to someone arriving in Quebec soon.',
    exploreExperiences: 'Explore experiences',
    viewMap: 'View map',
    offerGiftCard: 'Gift a Forfeo card',
    activateAlerts: 'Activate alerts',
    discoverLastCall: 'Discover Last Call',
    joinPresale: 'View presales',
    prepareStay: 'Plan my stay',
    routeLabel: 'Traveller journey',
    fromAbroad: 'From abroad',
    localPartner: 'Local partners',
    bilingual: 'Bilingual experience',
    simplePlanning: 'Simple planning',
  },
};

const profiles = {
  fr: [
    { icon: <FaHeart />, title: 'Couples', text: 'Spas, séjours romantiques, restaurants, surprises et escapades à deux.', href: '/boutique' },
    { icon: <FaTree />, title: 'Familles', text: 'Activités simples, plein air, découvertes locales et idées de fin de semaine.', href: '/carte' },
    { icon: <FaGlobeAmericas />, title: 'Visiteurs internationaux', text: 'Repères clairs, idées faciles à comprendre et parcours avant l’arrivée.', href: '/visiter-quebec' },
    { icon: <FaUtensils />, title: 'Gourmands', text: 'Restaurants, terroir, routes gourmandes, microbrasseries et expériences culinaires.', href: '/boutique' },
    { icon: <FaMountain />, title: 'Aventuriers', text: 'Plein air, régions, activités saisonnières et expériences à sensations.', href: '/carte' },
    { icon: <FaHotel />, title: 'Escapades détente', text: 'Hôtels, chalets, spas, cocooning et moments premium à réserver ou offrir.', href: '/explorer-plus' },
  ],
  en: [
    { icon: <FaHeart />, title: 'Couples', text: 'Spas, romantic stays, restaurants, surprises and two-person escapes.', href: '/boutique' },
    { icon: <FaTree />, title: 'Families', text: 'Simple activities, outdoors, local discoveries and weekend ideas.', href: '/carte' },
    { icon: <FaGlobeAmericas />, title: 'International visitors', text: 'Clear guidance, easy-to-understand ideas and pre-arrival planning.', href: '/visiter-quebec' },
    { icon: <FaUtensils />, title: 'Food lovers', text: 'Restaurants, terroir, foodie routes, microbreweries and culinary experiences.', href: '/boutique' },
    { icon: <FaMountain />, title: 'Adventure seekers', text: 'Outdoors, regions, seasonal activities and thrilling local experiences.', href: '/carte' },
    { icon: <FaHotel />, title: 'Relaxed escapes', text: 'Hotels, cabins, spas, cozy moments and premium stays to book or gift.', href: '/explorer-plus' },
  ],
};

const seasons = {
  fr: [
    { icon: <FaSnowflake />, name: 'Hiver', text: 'Spa nordique, chalet, motoneige, restos chaleureux, séjours cocooning.', tone: 'from-sky-500 to-blue-700' },
    { icon: <FaLeaf />, name: 'Printemps', text: 'Cabanes à sucre, nature qui revient, détente, découvertes locales.', tone: 'from-emerald-400 to-lime-600' },
    { icon: <FaSun />, name: 'Été', text: 'Terrasses, festivals, croisières, plein air, escapades gourmandes.', tone: 'from-amber-400 to-orange-600' },
    { icon: <FaMugHot />, name: 'Automne', text: 'Couleurs, vignobles, routes gourmandes, séjours romantiques.', tone: 'from-orange-500 to-rose-700' },
  ],
  en: [
    { icon: <FaSnowflake />, name: 'Winter', text: 'Nordic spas, cabins, snowmobiling, warm restaurants and cozy stays.', tone: 'from-sky-500 to-blue-700' },
    { icon: <FaLeaf />, name: 'Spring', text: 'Sugar shacks, returning nature, relaxation and local discoveries.', tone: 'from-emerald-400 to-lime-600' },
    { icon: <FaSun />, name: 'Summer', text: 'Terraces, festivals, cruises, outdoors and foodie escapes.', tone: 'from-amber-400 to-orange-600' },
    { icon: <FaMugHot />, name: 'Fall', text: 'Colours, vineyards, gourmet routes and romantic stays.', tone: 'from-orange-500 to-rose-700' },
  ],
};

const monetization = {
  fr: [
    { icon: <FaGift />, title: 'Carte Forfeo', text: 'Une carte numérique pour offrir le Québec local.', href: '/carte-cadeau', cta: 'Offrir' },
    { icon: <FaFire />, title: 'Last Call', text: 'Offres limitées et opportunités de dernière minute.', href: '/last-call', cta: 'Voir' },
    { icon: <FaTicketAlt />, title: 'Prévente', text: 'Accès à des forfaits et lancements limités.', href: '/prevente', cta: 'Découvrir' },
    { icon: <FaBell />, title: 'Alertes Évasion', text: 'Surveiller les occasions selon budget et destination.', href: '/evasion', cta: 'Activer' },
    { icon: <FaMapMarkedAlt />, title: 'Carte interactive', text: 'Visualiser les partenaires et planifier par région.', href: '/carte', cta: 'Explorer' },
    { icon: <FaShoppingBag />, title: 'Boutique', text: 'Forfaits, expériences et idées cadeaux locales.', href: '/boutique', cta: 'Magasiner' },
  ],
  en: [
    { icon: <FaGift />, title: 'Forfeo Card', text: 'A digital card to gift local Quebec.', href: '/carte-cadeau', cta: 'Gift' },
    { icon: <FaFire />, title: 'Last Call', text: 'Limited offers and last-minute opportunities.', href: '/last-call', cta: 'View' },
    { icon: <FaTicketAlt />, title: 'Presale', text: 'Access limited packages and launches.', href: '/prevente', cta: 'Discover' },
    { icon: <FaBell />, title: 'Évasion alerts', text: 'Track opportunities by budget and destination.', href: '/evasion', cta: 'Activate' },
    { icon: <FaMapMarkedAlt />, title: 'Interactive map', text: 'View partners and plan by region.', href: '/carte', cta: 'Explore' },
    { icon: <FaShoppingBag />, title: 'Shop', text: 'Local packages, experiences and gift ideas.', href: '/boutique', cta: 'Shop' },
  ],
};

const regions = {
  fr: [
    'Québec & Charlevoix',
    'Montréal & environs',
    'Cantons-de-l’Est',
    'Laurentides',
    'Mauricie',
    'Bas-Saint-Laurent',
  ],
  en: [
    'Quebec City & Charlevoix',
    'Montreal & surroundings',
    'Eastern Townships',
    'Laurentians',
    'Mauricie',
    'Lower St. Lawrence',
  ],
};

const journeySteps = {
  fr: [
    'Je découvre le Québec depuis mon pays',
    'Je sauvegarde des idées et active mes alertes',
    'Je réserve ou j’offre un moment Forfeo',
    'Je présente mon voucher chez le partenaire',
  ],
  en: [
    'I discover Quebec from my country',
    'I save ideas and activate my alerts',
    'I book or gift a Forfeo moment',
    'I present my voucher at the partner location',
  ],
};

function getLanguage(language?: string): DiscoverLanguage {
  return language?.toLowerCase().startsWith('en') ? 'en' : 'fr';
}

export default function DiscoverQuebec() {
  const { i18n } = useTranslation();
  const lang = getLanguage(i18n.resolvedLanguage || i18n.language);
  const c = copy[lang];

  const canonicalPath = useMemo(() => (lang === 'en' ? '/discover-quebec' : '/decouvrir-quebec'), [lang]);

  return (
    <main className="relative overflow-hidden bg-[#F7FAFC] text-slate-950">
      <SeoHead title={c.seoTitle} description={c.seoDescription} canonicalPath={canonicalPath} image="/lastcall-hero.jpg" />

      <section className="relative min-h-[88vh] overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(56,189,248,0.32),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(251,146,60,0.22),transparent_35%)]" />
        <div className="absolute inset-0 opacity-25 bg-[linear-gradient(115deg,transparent_0%,rgba(255,255,255,0.12)_45%,transparent_70%)]" />
        <div className="absolute -right-24 top-24 h-72 w-72 rounded-full bg-sky-400/20 blur-3xl" />
        <div className="absolute -left-24 bottom-10 h-72 w-72 rounded-full bg-orange-400/20 blur-3xl" />

        <div className="container relative mx-auto px-4 pb-20 pt-24 md:pt-32">
          <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}>
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-[0.22em] text-sky-100 backdrop-blur">
                <FaGlobeAmericas className="text-sky-300" /> {c.eyebrow}
              </div>
              <h1 className="max-w-5xl text-5xl font-black leading-[0.92] tracking-tight md:text-7xl lg:text-8xl">
                {c.title}{' '}
                <span className="block bg-gradient-to-r from-sky-200 via-white to-orange-200 bg-clip-text text-transparent">
                  {c.titleAccent}
                </span>
              </h1>
              <p className="mt-8 max-w-3xl text-lg leading-8 text-slate-200 md:text-xl">
                {c.subtitle}
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <Link to="/boutique" className="inline-flex items-center justify-center gap-3 rounded-2xl bg-white px-6 py-4 text-sm font-black uppercase tracking-widest text-slate-950 shadow-2xl shadow-sky-950/40 transition hover:-translate-y-0.5 hover:bg-sky-50">
                  {c.primaryCta} <FaArrowRight />
                </Link>
                <Link to="/carte" className="inline-flex items-center justify-center gap-3 rounded-2xl border border-white/20 bg-white/10 px-6 py-4 text-sm font-black uppercase tracking-widest text-white backdrop-blur transition hover:-translate-y-0.5 hover:bg-white/15">
                  {c.secondaryCta} <FaMapMarkedAlt />
                </Link>
                <Link to="/evasion" className="inline-flex items-center justify-center gap-3 rounded-2xl border border-sky-300/30 bg-sky-400/10 px-6 py-4 text-sm font-black uppercase tracking-widest text-sky-100 backdrop-blur transition hover:-translate-y-0.5 hover:bg-sky-400/20">
                  {c.alertCta} <FaBell />
                </Link>
              </div>

              <div className="mt-10 grid max-w-2xl grid-cols-3 gap-3">
                {c.stats.map((stat) => (
                  <div key={stat.label} className="rounded-3xl border border-white/10 bg-white/10 p-4 backdrop-blur">
                    <p className="text-xl font-black md:text-2xl">{stat.value}</p>
                    <p className="mt-1 text-[11px] font-bold uppercase tracking-widest text-slate-300">{stat.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.55, delay: 0.1 }} className="relative">
              <div className="absolute -inset-5 rounded-[3rem] bg-gradient-to-br from-sky-400/25 via-white/10 to-orange-400/25 blur-2xl" />
              <div className="relative overflow-hidden rounded-[2.5rem] border border-white/15 bg-white/10 p-5 shadow-2xl backdrop-blur-xl">
                <div className="h-80 overflow-hidden rounded-[2rem] bg-[url('/lastcall-hero.jpg')] bg-cover bg-center shadow-inner md:h-[28rem]">
                  <div className="flex h-full flex-col justify-between bg-gradient-to-t from-slate-950/90 via-slate-950/20 to-transparent p-6">
                    <span className="w-fit rounded-full bg-white/95 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-slate-950 shadow-lg">
                      {c.heroBadge}
                    </span>
                    <div>
                      <h2 className="text-3xl font-black leading-tight">{c.heroCardTitle}</h2>
                      <p className="mt-3 text-sm leading-6 text-slate-200">{c.heroCardText}</p>
                    </div>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-3">
                  <div className="rounded-2xl bg-white p-4 text-slate-950"><FaPlane className="mb-2 text-sky-500" /><p className="text-[10px] font-black uppercase tracking-widest">{c.fromAbroad}</p></div>
                  <div className="rounded-2xl bg-white p-4 text-slate-950"><FaConciergeBell className="mb-2 text-orange-500" /><p className="text-[10px] font-black uppercase tracking-widest">{c.localPartner}</p></div>
                  <div className="rounded-2xl bg-white p-4 text-slate-950"><FaRoute className="mb-2 text-emerald-500" /><p className="text-[10px] font-black uppercase tracking-widest">{c.simplePlanning}</p></div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 md:py-24">
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.25em] text-blue-600">Forfeo Explore</p>
            <h2 className="mt-3 text-4xl font-black tracking-tight md:text-5xl">{c.introTitle}</h2>
          </div>
          <p className="text-lg leading-8 text-slate-600">{c.introText}</p>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="mb-10 max-w-3xl">
            <h2 className="text-4xl font-black tracking-tight md:text-5xl">{c.profileTitle}</h2>
            <p className="mt-4 text-lg leading-8 text-slate-600">{c.profileSubtitle}</p>
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {profiles[lang].map((profile) => (
              <Link key={profile.title} to={profile.href} className="group rounded-[2rem] border border-slate-100 bg-slate-50 p-6 shadow-sm transition hover:-translate-y-1 hover:border-blue-100 hover:bg-white hover:shadow-xl">
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-950 text-white shadow-lg transition group-hover:rotate-3 group-hover:bg-blue-600">
                  {profile.icon}
                </div>
                <h3 className="text-xl font-black text-slate-950">{profile.title}</h3>
                <p className="mt-3 min-h-[72px] text-sm leading-6 text-slate-600">{profile.text}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-black text-blue-600">
                  {c.exploreExperiences} <FaChevronRight className="transition group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 md:py-24">
        <div className="mb-10 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="max-w-3xl">
            <h2 className="text-4xl font-black tracking-tight md:text-5xl">{c.seasonTitle}</h2>
            <p className="mt-4 text-lg leading-8 text-slate-600">{c.seasonSubtitle}</p>
          </div>
          <Link to="/boutique" className="inline-flex w-fit items-center gap-3 rounded-2xl bg-slate-950 px-6 py-4 text-sm font-black uppercase tracking-widest text-white transition hover:-translate-y-0.5 hover:bg-blue-700">
            {c.exploreExperiences} <FaArrowRight />
          </Link>
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {seasons[lang].map((season) => (
            <div key={season.name} className={`rounded-[2rem] bg-gradient-to-br ${season.tone} p-6 text-white shadow-xl shadow-slate-200`}>
              <div className="mb-10 flex h-12 w-12 items-center justify-center rounded-2xl bg-white/20 text-2xl backdrop-blur">
                {season.icon}
              </div>
              <h3 className="text-2xl font-black">{season.name}</h3>
              <p className="mt-3 text-sm leading-6 text-white/90">{season.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-950 py-16 text-white md:py-24">
        <div className="container mx-auto px-4">
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-widest text-sky-100">
                <FaMapMarkedAlt /> {c.regionsTitle}
              </div>
              <h2 className="text-4xl font-black tracking-tight md:text-5xl">{c.regionsTitle}</h2>
              <p className="mt-5 text-lg leading-8 text-slate-300">{c.regionsSubtitle}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/carte" className="rounded-2xl bg-white px-6 py-4 text-sm font-black uppercase tracking-widest text-slate-950 transition hover:-translate-y-0.5 hover:bg-sky-50">{c.viewMap}</Link>
                <Link to="/visiter-quebec" className="rounded-2xl border border-white/15 bg-white/10 px-6 py-4 text-sm font-black uppercase tracking-widest text-white transition hover:bg-white/15">{c.prepareStay}</Link>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {regions[lang].map((region, index) => (
                <div key={region} className="rounded-3xl border border-white/10 bg-white/10 p-5 backdrop-blur">
                  <p className="text-xs font-black uppercase tracking-[0.25em] text-sky-200">0{index + 1}</p>
                  <p className="mt-4 text-xl font-black">{region}</p>
                  <p className="mt-2 text-sm text-slate-300">{c.routeLabel}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 md:py-24">
        <div className="mb-10 max-w-4xl">
          <h2 className="text-4xl font-black tracking-tight md:text-5xl">{c.monetizationTitle}</h2>
          <p className="mt-4 text-lg leading-8 text-slate-600">{c.monetizationSubtitle}</p>
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {monetization[lang].map((item) => (
            <Link key={item.title} to={item.href} className="group overflow-hidden rounded-[2rem] border border-slate-100 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-2xl">
              <div className="mb-6 flex items-center justify-between">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-xl text-blue-600 transition group-hover:bg-blue-600 group-hover:text-white">
                  {item.icon}
                </div>
                <FaArrowRight className="text-slate-300 transition group-hover:translate-x-1 group-hover:text-blue-600" />
              </div>
              <h3 className="text-xl font-black text-slate-950">{item.title}</h3>
              <p className="mt-3 min-h-[52px] text-sm leading-6 text-slate-600">{item.text}</p>
              <span className="mt-6 inline-flex rounded-full bg-slate-100 px-4 py-2 text-xs font-black uppercase tracking-widest text-slate-700 transition group-hover:bg-blue-50 group-hover:text-blue-700">
                {item.cta}
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="grid gap-10 lg:grid-cols-[1fr_0.8fr] lg:items-center">
            <div>
              <h2 className="text-4xl font-black tracking-tight md:text-5xl">{c.journeyTitle}</h2>
              <p className="mt-4 text-lg leading-8 text-slate-600">{c.journeySubtitle}</p>
              <div className="mt-8 space-y-4">
                {journeySteps[lang].map((step, index) => (
                  <div key={step} className="flex gap-4 rounded-3xl border border-slate-100 bg-slate-50 p-5">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-sm font-black text-white">{index + 1}</div>
                    <div>
                      <p className="font-black text-slate-950">{step}</p>
                      <p className="mt-1 text-sm text-slate-500">Forfeo Explore</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-[2.5rem] bg-gradient-to-br from-blue-600 to-slate-950 p-8 text-white shadow-2xl">
              <FaCompass className="text-4xl text-sky-200" />
              <h3 className="mt-6 text-3xl font-black">{c.reassuranceTitle}</h3>
              <p className="mt-4 text-sm leading-7 text-blue-100">{c.reassuranceSubtitle}</p>
              <div className="mt-8 space-y-3">
                {[c.bilingual, c.localPartner, c.simplePlanning].map((label) => (
                  <div key={label} className="flex items-center gap-3 rounded-2xl bg-white/10 p-4 text-sm font-bold backdrop-blur">
                    <FaCheckCircle className="text-emerald-300" /> {label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 md:py-24">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-slate-950 p-8 text-white shadow-2xl md:p-12">
          <div className="absolute -right-10 -top-10 h-64 w-64 rounded-full bg-sky-400/25 blur-3xl" />
          <div className="absolute -bottom-16 -left-16 h-64 w-64 rounded-full bg-orange-400/20 blur-3xl" />
          <div className="relative grid gap-8 lg:grid-cols-[1fr_auto] lg:items-center">
            <div>
              <h2 className="text-4xl font-black tracking-tight md:text-5xl">{c.finalTitle}</h2>
              <p className="mt-4 max-w-3xl text-lg leading-8 text-slate-300">{c.finalSubtitle}</p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
              <Link to="/boutique" className="inline-flex items-center justify-center gap-3 rounded-2xl bg-white px-6 py-4 text-sm font-black uppercase tracking-widest text-slate-950 transition hover:-translate-y-0.5 hover:bg-sky-50">
                {c.exploreExperiences} <FaArrowRight />
              </Link>
              <Link to="/carte-cadeau" className="inline-flex items-center justify-center gap-3 rounded-2xl border border-white/15 bg-white/10 px-6 py-4 text-sm font-black uppercase tracking-widest text-white transition hover:bg-white/15">
                {c.offerGiftCard} <FaGift />
              </Link>
            </div>
          </div>
        </div>
      </section>
    <section
      id="forfy-mini-guide"
      data-forfy-mini-guide="canonical"
      className="relative overflow-hidden bg-slate-950 px-4 py-14 text-white sm:px-6 lg:px-8"
    >
      <div className="mx-auto max-w-6xl">
        <div className="mb-7 text-center">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-cyan-300">
            {lang === 'en' ? 'Forfy AI concierge' : 'Concierge IA Forfy'}
          </p>
          <h2 className="mt-3 text-3xl font-black tracking-tight sm:text-4xl">
            {lang === 'en'
              ? 'Plan your Quebec discovery with Forfy'
              : 'Planifie ta découverte du Québec avec Forfy'}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm font-medium leading-relaxed text-slate-300 sm:text-base">
            {lang === 'en'
              ? 'Choose a quick idea and let Forfy prepare a practical mini-guide for your visit.'
              : 'Choisis une idée rapide et laisse Forfy préparer un mini-guide pratique pour ta visite.'}
          </p>
        </div>
        <MiniGuideAI
          cityLabel={lang === 'en' ? 'Quebec' : 'Québec'}
        />
      </div>
    </section>

    </main>
  );
}
